/** Terrain analysis block: key numbers, surface mix, climbs table, gradient histogram. */
import type { Analysis } from "@/api/schemas";
import { CLIMB_COLOR } from "@/lib/surface";
import { km, meters, num, pct } from "@/lib/format";
import { SurfaceLegend } from "./RouteMap";
import { Badge } from "./ui";

export function ClimbTable({ climbs }: { climbs: Analysis["climbs"] }) {
  if (climbs.length === 0) return <p className="muted">No categorised climbs.</p>;
  return (
    <div className="table-wrap" tabIndex={0} role="region" aria-label="Route surface and climbs">
      <table aria-label="Climbs">
        <thead>
          <tr>
            <th>Cat</th>
            <th className="r">At km</th>
            <th className="r">Length</th>
            <th className="r">Gain</th>
            <th className="r">Avg</th>
            <th className="r">Max</th>
            <th className="r">Top</th>
            <th className="r">Unpaved</th>
          </tr>
        </thead>
        <tbody>
          {climbs.map((c, i) => (
            <tr key={i}>
              <td>
                <span className="badge cat" style={{ ["--cat" as string]: CLIMB_COLOR[c.category] }}>
                  {c.category === "HC" ? "HC" : `Cat ${c.category}`}
                </span>
              </td>
              <td className="r">{num(c.start_km, 1)}</td>
              <td className="r">{km(c.length_km, 1)}</td>
              <td className="r">{meters(c.gain_m)}</td>
              <td className="r">{pct(c.avg_grade_pct, 1)}</td>
              <td className="r">{pct(c.max_grade_pct, 0)}</td>
              <td className="r">{meters(c.top_ele_m)}</td>
              <td className="r">{pct(c.unpaved_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function GradientHistogram({ hist }: { hist: Analysis["gradient_hist"] }) {
  const max = Math.max(0.01, ...hist.map((h) => h.km));
  return (
    <div className="stack" style={{ gap: 4 }} aria-label="Gradient distribution">
      {hist.map((h) => (
        <div key={h.bucket} className="row" style={{ gap: 8, flexWrap: "nowrap", fontSize: 12 }}>
          <span className="mono" style={{ width: 64, textAlign: "right", color: "var(--muted)" }}>
            {h.bucket}%
          </span>
          <div className="bar" style={{ flex: 1, marginTop: 0 }}>
            <span
              style={{
                width: `${(h.km / max) * 100}%`,
                background: /^(9|12|>15)/.test(h.bucket) ? "var(--bad)" : "var(--accent)",
              }}
            />
          </div>
          <span className="mono" style={{ width: 56 }}>
            {num(h.km, 1)} km
          </span>
        </div>
      ))}
    </div>
  );
}

export function AnalysisSummary({ a }: { a: Analysis }) {
  return (
    <div className="stack">
      <div className="kpi-row">
        <span>
          Distance <b>{km(a.distance_km, 1)}</b>
        </span>
        <span>
          Climbing <b>{meters(a.ascent_m)}</b>
        </span>
        <span>
          Density <b>{num(a.climbing_density_m_per_km)} m/km</b>
        </span>
        <span>
          Unpaved <b>{pct(a.unpaved_pct)}</b>
        </span>
        <span>
          Highest <b>{meters(a.max_ele_m)}</b>
        </span>
        {a.steep_unpaved_km != null && a.steep_unpaved_km >= 0.05 && (
          <span>
            Steep unpaved <b>{km(a.steep_unpaved_km, 1)}</b>
          </span>
        )}
        {a.hike_a_bike_km != null && a.hike_a_bike_km >= 0.05 && (
          <Badge tone="warn">Hike-a-bike ≈ {km(a.hike_a_bike_km, 1)}</Badge>
        )}
      </div>
      <SurfaceLegend analysis={a} />
      {a.surface_confidence_pct != null && (
        <small className="faint">
          Surface data confidence {pct(a.surface_confidence_pct)} (OSM tags; the rest is inferred from road
          type). Elevation: {a.elevation_source}.
        </small>
      )}
    </div>
  );
}
